import { array } from 'fp-ts';
import { LocalizedText, useDialogAction } from '@mews/framework';
import { TextKey } from '@mews-commander/enums';
import { PortfolioVoucherOverview } from '@mews-commander/data-access';
import { Dialog, IconName, ActionToolbarButton } from '@optimus-web/core';
import { useDeleteVouchersMutation } from '../../../../queries';
import { DELETE_VOUCHER_ENDING_POINT, DELETE_VOUCHER_STARTING_POINT } from '../../../../utilities/userFlowTestIds';

interface VoucherDeleteWrapperProps {
    selectedVouchers: PortfolioVoucherOverview[];
    onSuccess: () => void;
}

export const VoucherDeleteWrapper = ({ selectedVouchers, onSuccess }: VoucherDeleteWrapperProps) => {
    const deleteVoucherMutation = useDeleteVouchersMutation();

    // eslint-disable-next-line @typescript-eslint/require-await
    const deleteAction = async () => {
        deleteVoucherMutation.mutate(selectedVouchers);
        onSuccess?.();
    };
    const { states: deleteStates, handlers: deleteHandlers } = useDialogAction(deleteAction);
    const disabled = array.isEmpty(selectedVouchers);

    const dialogTitle =
        selectedVouchers.length > 1 ? (
            <LocalizedText textKey={TextKey.VoucherDeleteBulkTitle} />
        ) : (
            <LocalizedText
                textKey={TextKey.VoucherDeleteSingleTitle}
                substitutes={{ voucherName: selectedVouchers[0]?.name }}
            />
        );

    const dialogContentTextKey =
        selectedVouchers.length > 1 ? TextKey.VoucherDeleteBulkDescription : TextKey.VoucherDeleteSingleDescription;

    return (
        <>
            <ActionToolbarButton
                disabled={disabled}
                icon={IconName.Delete}
                onClick={deleteHandlers.handleOpen}
                data-test-id={DELETE_VOUCHER_STARTING_POINT}
            >
                <LocalizedText textKey={TextKey.Delete} />
            </ActionToolbarButton>
            {deleteStates.isDialogOpened && (
                <Dialog
                    title={dialogTitle}
                    actions={[
                        {
                            children: <LocalizedText textKey={TextKey.Cancel} />,
                            onClick: deleteHandlers.handleCancel,
                            variant: 'tertiary',
                            size: 'medium',
                            type: 'button',
                        },
                        {
                            children: <LocalizedText textKey={TextKey.Delete} />,
                            onClick: deleteHandlers.handleConfirm,
                            variant: 'destructive',
                            size: 'medium',
                            'data-test-id': DELETE_VOUCHER_ENDING_POINT,
                            type: 'submit',
                        },
                    ]}
                    active
                    loading={deleteStates.isProcessing}
                    onClose={deleteHandlers.handleCancel}
                >
                    <LocalizedText textKey={dialogContentTextKey} />
                </Dialog>
            )}
        </>
    );
};
